import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faTrash, faChevronDown, faChevronRight, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { discardPublication, fetchPostDetail, fetchPublications, refreshProjectMetrics, refreshPublication, updatePublicationMetrics } from "../../redux/marketingSlice";
import Popup from "../../components/Popup/Popup";
import { safeUrl } from "../../utils/url";
import { PLATFORMS, formatNumber, formatPercent } from "./marketingUtils";

const MILESTONES = [
    { key: "current", label: "Actual" },
    { key: "metrics24h", label: "24 h" },
    { key: "metrics72h", label: "72 h" },
    { key: "metrics7d", label: "7 días" },
];

const METRIC_STATUS = {
    pending: "Pendiente",
    captured: "Capturada",
    missed: "No capturada",
    manual: "Manual",
};

// Likes por cada 100 vistas; la API ya lo entrega como likeRate.
const likeRateOf = (metrics) => {
    if (metrics?.likeRate !== undefined && metrics?.likeRate !== null) return metrics.likeRate;
    return metrics?.views && metrics?.likes !== null && metrics?.likes !== undefined
        ? Math.round((metrics.likes / metrics.views) * 10000) / 100
        : null;
};

// El valor real (Instagram o cargado a mano) tiene prioridad; en YouTube, si no
// hay, se estima con vistas comprometidas ÷ vistas de YouTube Analytics.
const stayedToWatchOf = (row) => {
    if (row.stayedToWatch !== null && row.stayedToWatch !== undefined) return { value: row.stayedToWatch, estimated: false };
    if (row.engagedViewRate !== null && row.engagedViewRate !== undefined) return { value: row.engagedViewRate, estimated: true };
    return { value: null, estimated: false };
};

const formatStayed = (row) => {
    const { value, estimated } = stayedToWatchOf(row);
    return estimated ? `≈ ${formatPercent(value)}` : formatPercent(value);
};

// ── Outlier (como el de vidIQ) ───────────────────────────────────────────────

const MIN_OUTLIER_SAMPLE = 3;

export const latestViews = (row) =>
    [row.current?.views, row.metrics7d?.views, row.metrics72h?.views, row.metrics24h?.views].find(
        (value) => value !== null && value !== undefined
    ) ?? null;

const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

// Compara cada video contra la mediana de los demás videos de su misma cuenta
// (o red, si no tiene cuenta conectada). Se usa la mediana para que un viral no
// infle la referencia. Hace falta un mínimo de videos para que sea confiable.
const withOutliers = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
        const views = latestViews(row);
        if (views === null) return;
        const key = row.accountId || row.platform;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ key: row.key, views });
    });
    return rows.map((row) => {
        const views = latestViews(row);
        const others = (groups.get(row.accountId || row.platform) || []).filter((item) => item.key !== row.key);
        if (views === null || others.length < MIN_OUTLIER_SAMPLE) return { ...row, outlier: null };
        const baseline = median(others.map((item) => item.views));
        return { ...row, outlier: baseline > 0 ? Math.round((views / baseline) * 10) / 10 : null };
    });
};

const outlierFormat = new Intl.NumberFormat("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function formatOutlier(value) {
    return value === null || value === undefined ? "—" : `${outlierFormat.format(value)}x`;
}

// Datos que la red no entrega por API con la conexión actual (TikTok usa la
// Display API). Se muestran como "N/D" para no confundirlos con "aún sin medir".
const UNAVAILABLE = {
    tiktok: ["saves", "retention"],
    youtube: ["saves"],
};

const NOT_AVAILABLE_LABEL = "N/D";
const NOT_AVAILABLE_HINT = "Esta red no entrega este dato por su API";

const isUnavailable = (platform, key, value) =>
    (value === null || value === undefined) && (UNAVAILABLE[platform] || []).includes(key);

const COLUMNS = [
    { key: "publishDate", label: "Fecha", value: (row) => (row.publishDate ? new Date(row.publishDate).getTime() : null) },
    { key: "views", label: "Vistas", value: (row, m) => row[m]?.views },
    {
        key: "growth",
        label: "Vistas desde 7 d",
        value: (row) =>
            row.current?.views !== null && row.current?.views !== undefined && row.metrics7d?.views !== null && row.metrics7d?.views !== undefined
                ? row.current.views - row.metrics7d.views
                : null,
        hint: "Vistas actuales menos las del día 7: muestra si el video sigue creciendo",
    },
    { key: "likes", label: "Likes", value: (row, m) => row[m]?.likes },
    { key: "likeRate", label: "Like rate", value: (row, m) => likeRateOf(row[m]), format: formatPercent, hint: "Likes ÷ vistas" },
    { key: "comments", label: "Comentarios", value: (row, m) => row[m]?.comments },
    { key: "shares", label: "Compartidos", value: (row, m) => row[m]?.shares },
    { key: "saves", label: "Guardados", value: (row, m) => row[m]?.saves },
    {
        key: "stayedToWatch",
        label: "Stayed to watch",
        value: (row) => stayedToWatchOf(row).value,
        format: formatPercent,
        hint: "% que se quedó a ver en vez de deslizar. ≈ = estimado en YouTube con vistas comprometidas ÷ vistas",
    },
    { key: "retention", label: "Retención", value: (row) => row.retention, format: formatPercent, hint: "YouTube e Instagram (Reels): % promedio visto · TikTok: % que lo vio completo" },
    {
        key: "outlier",
        label: "Outlier",
        value: (row) => row.outlier ?? null,
        format: formatOutlier,
        hint: "Vistas del video ÷ vistas típicas (mediana) de los demás videos de la misma cuenta. 2x = el doble de lo normal",
    },
];

// Portada del video. YouTube se arma con el ID; Instagram y TikTok la manda la API
// (enlaces que vencen y se renuevan en cada actualización).
const thumbnailOf = (row) =>
    row.thumbnailUrl || (row.platform === "youtube" && row.externalId ? `https://i.ytimg.com/vi/${row.externalId}/mqdefault.jpg` : null);

export function Thumbnail({ row }) {
    const [failed, setFailed] = useState(false);
    const src = thumbnailOf(row);
    const platform = PLATFORMS[row.platform];

    if (!src || failed) {
        return (
            <span className="mk-thumb mk-thumb--empty" aria-hidden="true">
                {platform && <FontAwesomeIcon icon={platform.icon} />}
            </span>
        );
    }
    return <img className="mk-thumb" src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
}

function SortHeader({ column, sort, onSort }) {
    const active = sort.key === column.key;
    const icon = !active ? faSort : sort.dir === "asc" ? faSortUp : faSortDown;
    return (
        <th className={column.key === "publishDate" ? "" : "mk-num"} title={column.hint} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
            <button type="button" className="mk-sort" onClick={() => onSort(column.key)}>
                {column.label} <FontAwesomeIcon icon={icon} />
            </button>
        </th>
    );
}

function AudienceList({ title, items, labelKey }) {
    if (!items?.length) return null;
    return (
        <div>
            <h5 className="mk-detail-title">{title}</h5>
            {[...items]
                .sort((a, b) => b.percentage - a.percentage)
                .slice(0, 6)
                .map((item) => (
                    <div key={item[labelKey]} className="mk-detail-bar">
                        <span>{item[labelKey]}</span>
                        <span className="mk-bar"><span className="mk-bar-fill" style={{ width: `${item.percentage}%` }} /></span>
                        <span className="mk-bar-label">{formatPercent(item.percentage)}</span>
                    </div>
                ))}
        </div>
    );
}

// YouTube y TikTok no exponen "stayed to watch" por API: se copia de Studio.
function StayedToWatchEditor({ row, onSaved }) {
    const dispatch = useDispatch();
    const [value, setValue] = useState(row.stayedToWatch ?? "");
    const [status, setStatus] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const number = value === "" ? null : Number(value);
        if (number !== null && (Number.isNaN(number) || number < 0 || number > 100)) {
            setStatus({ error: "Debe ser un número entre 0 y 100" });
            return;
        }
        setStatus({ saving: true });
        try {
            const post = await dispatch(
                updatePublicationMetrics({ row, changes: { stayedToWatch: number } })
            ).unwrap();
            onSaved(row.unassigned ? post : post.platforms?.find((item) => item._id === row._id));
            setStatus({ saved: true });
        } catch (error) {
            setStatus({ error });
        }
    };

    return (
        <form className="mk-inline-form mk-inline-form--small" onSubmit={handleSubmit}>
            <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={value}
                onChange={(event) => { setValue(event.target.value); setStatus(null); }}
                placeholder={row.platform === "tiktok" ? "% de TikTok Studio" : "% de YouTube Studio"}
                aria-label="Stayed to watch en porcentaje"
            />
            <button type="submit" className="mk-small-btn" disabled={status?.saving}>Guardar</button>
            {status?.saved && <span className="mk-muted">Guardado</span>}
            {status?.error && <span className="mk-error">{String(status.error)}</span>}
        </form>
    );
}

function PublicationDetail({ row }) {
    const dispatch = useDispatch();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (row.unassigned) return;
        dispatch(fetchPostDetail({ id: row.postId }))
            .unwrap()
            .then((post) => setDetail(post.platforms?.find((item) => item._id === row._id) || null))
            .catch(setError);
    }, [dispatch, row]);

    const [refreshing, setRefreshing] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const handleDiscard = async () => {
        try {
            await dispatch(discardPublication({ row })).unwrap();
        } catch (discardError) {
            setError(discardError);
            setConfirmDiscard(false);
        }
    };

    const publication = { ...row, ...(detail || {}) };

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const updated = await dispatch(refreshPublication({ row })).unwrap();
            if (!row.unassigned) {
                const match = updated?.platforms?.find((item) => item._id === row._id);
                if (match) setDetail(match);
            }
        } catch (refreshError) {
            setError(refreshError);
        } finally {
            setRefreshing(false);
        }
    };
    const text = publication.description || publication.copy;
    const audience = publication.audience;

    return (
        <div className="mk-detail">
            <div className="mk-detail-actions">
                <span className="mk-muted">
                    Métricas actuales: {publication.current?.capturedAt ? `actualizadas el ${new Date(publication.current.capturedAt).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}` : "aún no se han consultado"}
                </span>
                <span className="mk-actions">
                    {publication.accountId && (
                        <button type="button" className="mk-small-btn" onClick={handleRefresh} disabled={refreshing}>
                            <FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} /> Actualizar ahora
                        </button>
                    )}
                    {row.unassigned && (
                        <button type="button" className="mk-small-btn secundary" onClick={() => setConfirmDiscard(true)}>
                            <FontAwesomeIcon icon={faTrash} /> Descartar
                        </button>
                    )}
                </span>
                <Popup
                    isOpen={confirmDiscard}
                    title="Descartar publicación"
                    onClose={() => setConfirmDiscard(false)}
                    onConfirm={handleDiscard}
                    confirmText="Descartar"
                >
                    <p>
                        "{row.postTitle}" dejará de aparecer en el panel y de contarse en las métricas. Úsalo para videos borrados o
                        duplicados.
                    </p>
                </Popup>
            </div>
            <div className="mk-table-wrap">
                <table className="mk-table mk-table--compact">
                    <thead>
                        <tr>
                            <th>Momento</th>
                            <th className="mk-num">Vistas</th>
                            <th className="mk-num">Likes</th>
                            <th className="mk-num">Like rate</th>
                            <th className="mk-num">Comentarios</th>
                            <th className="mk-num">Compartidos</th>
                            <th className="mk-num">Guardados</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {MILESTONES.map((milestone) => {
                            const metrics = publication[milestone.key] || {};
                            return (
                                <tr key={milestone.key}>
                                    <td>{milestone.label}</td>
                                    <td className="mk-num">{formatNumber(metrics.views)}</td>
                                    <td className="mk-num">{formatNumber(metrics.likes)}</td>
                                    <td className="mk-num">{formatPercent(likeRateOf(metrics))}</td>
                                    <td className="mk-num">{formatNumber(metrics.comments)}</td>
                                    <td className="mk-num">{formatNumber(metrics.shares)}</td>
                                    <td className="mk-num" title={isUnavailable(publication.platform, "saves", metrics.saves) ? NOT_AVAILABLE_HINT : undefined}>
                                        {isUnavailable(publication.platform, "saves", metrics.saves) ? NOT_AVAILABLE_LABEL : formatNumber(metrics.saves)}
                                    </td>
                                    <td>
                                        {milestone.key === "current" ? (metrics.capturedAt ? "Actualizada" : "Sin datos") : METRIC_STATUS[metrics.status] || "—"}
                                        {metrics.capturedAt && (
                                            <span className="mk-muted"> · {new Date(metrics.capturedAt).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mk-detail-grid">
                <div>
                    <h5 className="mk-detail-title">Retención</h5>
                    <p>
                        Stayed to watch: <strong>{formatStayed(publication)}</strong>
                        {stayedToWatchOf(publication).estimated && (
                            <>
                                <br />
                                <span className="mk-muted">
                                    Estimado: {formatNumber(publication.engagedViews)} vistas comprometidas de {formatNumber(publication.analyticsViews)} vistas.
                                    Si escribes el valor de YouTube Studio, reemplaza la estimación.
                                </span>
                            </>
                        )}
                    </p>
                    {publication.platform !== "instagram" && (
                        <StayedToWatchEditor row={publication} onSaved={(updated) => updated && setDetail(updated)} />
                    )}
                    <p>
                        {publication.platform === "tiktok" ? "Vieron el video completo" : "Porcentaje promedio visto"}:{" "}
                        <strong>
                            {isUnavailable(publication.platform, "retention", publication.retention)
                                ? `${NOT_AVAILABLE_LABEL} (TikTok no lo entrega con la conexión actual)`
                                : formatPercent(publication.retention)}
                        </strong>
                        {publication.avgWatchTimeSec !== null && publication.avgWatchTimeSec !== undefined && (
                            <span className="mk-muted">
                                {" "}({formatNumber(publication.avgWatchTimeSec)} s vistos en promedio
                                {publication.videoDurationSec ? ` de ${formatNumber(publication.videoDurationSec)} s` : ""})
                            </span>
                        )}
                    </p>
                    {publication.hashtags?.length > 0 && (
                        <>
                            <h5 className="mk-detail-title">Hashtags</h5>
                            <p>{publication.hashtags.map((tag) => `#${tag}`).join(" ")}</p>
                        </>
                    )}
                    {text && (
                        <>
                            <h5 className="mk-detail-title">Texto</h5>
                            <p className="mk-detail-text">{text}</p>
                        </>
                    )}
                </div>
                {audience && (
                    <>
                        <AudienceList title="Edades" items={audience.ages} labelKey="ageRange" />
                        <AudienceList title="Países" items={audience.countries} labelKey="country" />
                    </>
                )}
            </div>
            {audience?.scope === "account" && <p className="mk-muted">La audiencia es de la cuenta completa; la plataforma no la da por video.</p>}
            {error && <p className="mk-error">{String(error)}</p>}
        </div>
    );
}

export default function Publications({ project, range }) {
    const dispatch = useDispatch();
    const request = useSelector((state) => state.marketing.publications);
    const [platform, setPlatform] = useState("all");
    const [milestone, setMilestone] = useState("current");
    const [sort, setSort] = useState({ key: "publishDate", dir: "desc" });
    const [expanded, setExpanded] = useState(null);
    const [bulk, setBulk] = useState(null);

    const handleRefreshAll = async () => {
        setBulk({ loading: true });
        try {
            const result = await dispatch(refreshProjectMetrics({ projectId: project._id })).unwrap();
            const parts = [`${result?.refreshed ?? 0} publicaciones actualizadas`];
            if (result?.failed) parts.push(`${result.failed} con error`);
            if (result?.skipped) parts.push(`${result.skipped} sin cuenta conectada`);
            setBulk({ text: parts.join(" · ") });
            dispatch(fetchPublications({ projectId: project._id }));
        } catch (error) {
            setBulk({ error: String(error) });
        }
    };

    useEffect(() => {
        dispatch(fetchPublications({ projectId: project._id }));
    }, [dispatch, project._id]);

    const rows = useMemo(() => {
        const from = new Date(`${range.from}T00:00:00Z`).getTime();
        const to = new Date(`${range.to}T23:59:59.999Z`).getTime();
        const column = COLUMNS.find((item) => item.key === sort.key);
        return withOutliers(request.data?.rows || [])
            .filter((row) => platform === "all" || row.platform === platform)
            .filter((row) => {
                const date = row.publishDate ? new Date(row.publishDate).getTime() : null;
                return date === null || (date >= from && date <= to);
            })
            .sort((a, b) => {
                const va = column.value(a, milestone);
                const vb = column.value(b, milestone);
                if (va === null || va === undefined) return 1;
                if (vb === null || vb === undefined) return -1;
                return sort.dir === "asc" ? va - vb : vb - va;
            });
    }, [request.data, platform, range, sort, milestone]);

    const handleSort = (key) =>
        setSort((current) => ({ key, dir: current.key === key && current.dir === "desc" ? "asc" : "desc" }));

    const platformsInData = [...new Set((request.data?.rows || []).map((row) => row.platform))];

    return (
        <section className="mk-panel">
            <h2 className="mk-panel-title">Videos y publicaciones</h2>
            <p className="mk-panel-subtitle">
                Métricas de cada publicación en el rango de fechas. Haz clic en una fila para ver el desglose.
            </p>

            <div className="mk-toolbar">
                <label className="mk-field">
                    <span>Red</span>
                    <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
                        <option value="all">Todas</option>
                        {platformsInData.map((key) => (
                            <option key={key} value={key}>{PLATFORMS[key]?.label || key}</option>
                        ))}
                    </select>
                </label>
                <label className="mk-field">
                    <span>Métricas</span>
                    <select value={milestone} onChange={(event) => setMilestone(event.target.value)}>
                        {MILESTONES.map((item) => (
                            <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                    </select>
                </label>
                <div className="mk-bulk">
                    <button type="button" className="mk-small-btn" onClick={handleRefreshAll} disabled={bulk?.loading}>
                        <FontAwesomeIcon icon={faArrowsRotate} spin={bulk?.loading} /> Actualizar métricas
                    </button>
                    {bulk?.text && <span className="mk-muted">{bulk.text}</span>}
                    {bulk?.error && <span className="mk-error">{bulk.error}</span>}
                </div>
            </div>

            {(request.status === "idle" || (request.status === "loading" && !request.data)) && (
                <div className="mk-center"><div className="mk-spinner" /><span>Cargando publicaciones…</span></div>
            )}
            {request.status === "failed" && <div className="mk-center mk-error">{request.error}</div>}
            {request.data && rows.length === 0 && <div className="mk-center">No hay publicaciones en este rango.</div>}

            {request.data && rows.length > 0 && (
                <div className="mk-table-wrap">
                    <table className="mk-table">
                        <thead>
                            <tr>
                                <th>Publicación</th>
                                {COLUMNS.map((column) => (
                                    <SortHeader key={column.key} column={column} sort={sort} onSort={handleSort} />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isOpen = expanded === row.key;
                                return (
                                    <React.Fragment key={row.key}>
                                        <tr className="mk-row-click" onClick={() => setExpanded(isOpen ? null : row.key)} aria-expanded={isOpen}>
                                            <td>
                                                <span className="mk-pub">
                                                    <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="mk-muted" />
                                                    <Thumbnail key={thumbnailOf(row) || "none"} row={row} />
                                                    <span className="mk-pub-text">
                                                        <a href={safeUrl(row.url)} target="_blank" rel="noreferrer" className="mk-link" onClick={(event) => event.stopPropagation()}>
                                                            {row.postTitle}
                                                        </a>
                                                        <span className="mk-muted">
                                                            <span className="mk-swatch" style={{ background: PLATFORMS[row.platform]?.color }} />{" "}
                                                            {PLATFORMS[row.platform] && <FontAwesomeIcon icon={PLATFORMS[row.platform].icon} />} {PLATFORMS[row.platform]?.label || row.platform}
                                                        </span>
                                                    </span>
                                                </span>
                                            </td>
                                            {COLUMNS.map((column) => {
                                                const value = column.value(row, milestone);
                                                if (column.key === "publishDate") {
                                                    return <td key={column.key}>{value ? new Date(value).toLocaleDateString("es-CO") : "—"}</td>;
                                                }
                                                if (column.key === "outlier") {
                                                    const outlierClass = value >= 2 ? " mk-outlier--high" : value !== null && value < 0.5 ? " mk-outlier--low" : "";
                                                    return (
                                                        <td key={column.key} className={`mk-num${outlierClass}`}>
                                                            {formatOutlier(value)}
                                                        </td>
                                                    );
                                                }
                                                if (column.key === "stayedToWatch") {
                                                    return (
                                                        <td key={column.key} className="mk-num" title={stayedToWatchOf(row).estimated ? "Estimado con vistas comprometidas ÷ vistas" : undefined}>
                                                            {formatStayed(row)}
                                                        </td>
                                                    );
                                                }
                                                if (isUnavailable(row.platform, column.key, value)) {
                                                    return (
                                                        <td key={column.key} className="mk-num mk-na" title={NOT_AVAILABLE_HINT}>
                                                            {NOT_AVAILABLE_LABEL}
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td key={column.key} className="mk-num">
                                                        {(column.format || formatNumber)(value ?? null)}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {isOpen && (
                                            <tr className="mk-detail-row">
                                                <td colSpan={COLUMNS.length + 1}>
                                                    <PublicationDetail row={row} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {request.data?.truncated && (
                <p className="mk-muted">Se muestran las 1.000 publicaciones más recientes del proyecto.</p>
            )}
        </section>
    );
}
